'use client';

import React, { useState } from 'react';
import {
  CheckCircle2, XCircle, Package, Hash, Calendar, Layers,
  AlertTriangle, ChevronDown, ChevronUp, ShieldCheck, Clock, Filter
} from 'lucide-react';

interface QCItem {
  id: string;
  grnId: string;
  itemName: string;
  batchNumber: string;
  mfgDate: string;
  expiryDate: string;
  quantity: string;
  uom: string;
  rack: string;
  remarks: string;
  addedAt: string;
  shelfLifePct: number | null;
  status: 'pending' | 'approved' | 'rejected';
  qcNote?: string;
}

const MOCK_PENDING_ITEMS: QCItem[] = [
  {
    id: 'INV-12345601',
    grnId: 'GRN-20260612-001',
    itemName: 'Non Woven Tape 75mm x 250cm',
    batchNumber: 'BAT-2026-001',
    mfgDate: '2025-06-01',
    expiryDate: '2027-06-01',
    quantity: '100',
    uom: 'ROLL',
    rack: 'A-01-02-B-05',
    remarks: '',
    addedAt: '10:30 AM',
    shelfLifePct: 82,
    status: 'pending',
  },
  {
    id: 'INV-12345602',
    grnId: 'GRN-20260612-001',
    itemName: 'Cling Drape 15cm x 500cm',
    batchNumber: 'BAT-2026-002',
    mfgDate: '2025-01-15',
    expiryDate: '2026-08-15',
    quantity: '50',
    uom: 'ROLL',
    rack: 'A-01-03-C-02',
    remarks: 'Check for packaging damage',
    addedAt: '10:32 AM',
    shelfLifePct: 34,
    status: 'pending',
  },
  {
    id: 'INV-12345603',
    grnId: 'GRN-20260612-002',
    itemName: 'Mayo S Trolley Cover Small',
    batchNumber: 'BAT-2026-003',
    mfgDate: '2025-09-01',
    expiryDate: '2028-09-01',
    quantity: '200',
    uom: 'PCS',
    rack: 'B-02-01-A-01',
    remarks: '',
    addedAt: '11:05 AM',
    shelfLifePct: 91,
    status: 'pending',
  },
  {
    id: 'INV-12345604',
    grnId: 'GRN-20260612-002',
    itemName: 'BP Scalpel Handle Size 4',
    batchNumber: 'AUTO-345604',
    mfgDate: '2024-12-01',
    expiryDate: 'No Expiry',
    quantity: '20',
    uom: 'PCS',
    rack: 'C-03-02-B-04',
    remarks: 'Sterile packaging intact',
    addedAt: '11:08 AM',
    shelfLifePct: null,
    status: 'pending',
  },
  {
    id: 'INV-12345605',
    grnId: 'GRN-20260611-087',
    itemName: 'Needle Holder Medium',
    batchNumber: 'BAT-2025-099',
    mfgDate: '2025-03-01',
    expiryDate: '2026-07-01',
    quantity: '15',
    uom: 'PCS',
    rack: '',
    remarks: 'Urgent — check batch',
    addedAt: '09:15 AM',
    shelfLifePct: 18,
    status: 'pending',
  },
];

function ShelfLifeBar({ pct }: { pct: number | null }) {
  if (pct === null) return <span className="text-2xs text-muted-foreground">No Expiry</span>;
  const color = pct >= 80 ? 'bg-primary' : pct >= 50 ? 'bg-accent' : 'bg-red-400';
  const textColor = pct >= 80 ? 'text-primary' : pct >= 50 ? 'text-accent' : 'text-red-400';
  return (
    <div className="flex items-center gap-2 min-w-0">
      <div className="flex-1 h-1.5 bg-muted rounded-full overflow-hidden min-w-[60px]">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-2xs font-bold tabular-nums ${textColor}`}>{pct}%</span>
    </div>
  );
}

export default function QCReviewBoard() {
  const [items, setItems] = useState<QCItem[]>(MOCK_PENDING_ITEMS);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [noteInputs, setNoteInputs] = useState<Record<string, string>>({});
  const [filterGRN, setFilterGRN] = useState<string>('all');

  const uniqueGRNs = Array.from(new Set(items.map((i) => i.grnId)));

  const filteredItems = filterGRN === 'all' ? items : items.filter((i) => i.grnId === filterGRN);

  const pendingCount = items.filter((i) => i.status === 'pending').length;
  const approvedCount = items.filter((i) => i.status === 'approved').length;
  const rejectedCount = items.filter((i) => i.status === 'rejected').length;

  const handleApprove = (id: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, status: 'approved', qcNote: noteInputs[id] || '' } : item
      )
    );
  };

  const handleReject = (id: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, status: 'rejected', qcNote: noteInputs[id] || '' } : item
      )
    );
  };

  const handleUndo = (id: string) => {
    setItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, status: 'pending', qcNote: undefined } : item
      )
    );
  };

  return (
    <div className="flex-1 p-5 space-y-4">

      {/* Stats bar */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-card border border-border rounded-xl px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center">
            <Clock size={16} className="text-accent" />
          </div>
          <div>
            <p className="text-hero-metric text-accent" style={{ fontSize: '1.5rem' }}>{pendingCount}</p>
            <p className="text-2xs text-muted-foreground">Pending</p>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
            <CheckCircle2 size={16} className="text-primary" />
          </div>
          <div>
            <p className="text-hero-metric text-primary" style={{ fontSize: '1.5rem' }}>{approvedCount}</p>
            <p className="text-2xs text-muted-foreground">Approved</p>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl px-4 py-3 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
            <XCircle size={16} className="text-red-400" />
          </div>
          <div>
            <p className="text-hero-metric text-red-400" style={{ fontSize: '1.5rem' }}>{rejectedCount}</p>
            <p className="text-2xs text-muted-foreground">Rejected</p>
          </div>
        </div>
      </div>

      {/* GRN Filter */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter size={13} className="text-muted-foreground" />
        <span className="text-xs text-muted-foreground">Filter by GRN:</span>
        <button
          onClick={() => setFilterGRN('all')}
          className={`px-3 py-1 rounded-full text-xs font-semibold transition-all ${filterGRN === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
        >
          All GRNs
        </button>
        {uniqueGRNs.map((grn) => (
          <button
            key={grn}
            onClick={() => setFilterGRN(grn)}
            className={`px-3 py-1 rounded-full text-xs font-mono font-semibold transition-all ${filterGRN === grn ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}
          >
            {grn}
          </button>
        ))}
      </div>

      {/* Items */}
      <div className="space-y-3">
        {filteredItems.length === 0 && (
          <div className="bg-card border border-border rounded-xl p-8 text-center">
            <ShieldCheck size={32} className="text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">No items to review.</p>
          </div>
        )}
        {filteredItems.map((item) => {
          const isExpanded = expandedId === item.id;
          const isLowShelf = item.shelfLifePct !== null && item.shelfLifePct < 50;
          const statusColors = {
            pending: 'border-border',
            approved: 'border-primary/40 bg-primary/3',
            rejected: 'border-red-500/40 bg-red-500/3',
          };

          return (
            <div
              key={item.id}
              className={`bg-card border rounded-xl overflow-hidden transition-all ${statusColors[item.status]}`}
            >
              {/* Item header row */}
              <div
                className="px-5 py-4 flex items-start gap-4 cursor-pointer"
                onClick={() => setExpandedId(isExpanded ? null : item.id)}
              >
                {/* Status icon */}
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5 ${
                  item.status === 'approved' ? 'bg-primary/15' :
                  item.status === 'rejected' ? 'bg-red-500/15' : 'bg-muted/50'
                }`}>
                  {item.status === 'approved' ? <CheckCircle2 size={18} className="text-primary" /> :
                   item.status === 'rejected' ? <XCircle size={18} className="text-red-400" /> :
                   <Package size={18} className="text-muted-foreground" />}
                </div>

                {/* Main info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-foreground leading-tight">{item.itemName}</p>
                      <div className="flex items-center gap-3 mt-1 flex-wrap">
                        <span className="text-2xs font-mono text-primary">{item.batchNumber}</span>
                        <span className="text-2xs text-muted-foreground">GRN: <span className="font-mono">{item.grnId}</span></span>
                        <span className="text-2xs text-muted-foreground">{item.addedAt}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {isLowShelf && (
                        <AlertTriangle size={14} className="text-red-400" title="Low shelf life" />
                      )}
                      {item.status === 'pending' ? (
                        <span className="badge-pending text-2xs font-bold px-2 py-0.5 rounded-full">PENDING</span>
                      ) : item.status === 'approved' ? (
                        <span className="badge-available text-2xs font-bold px-2 py-0.5 rounded-full">APPROVED</span>
                      ) : (
                        <span className="badge-damaged text-2xs font-bold px-2 py-0.5 rounded-full">REJECTED</span>
                      )}
                      {isExpanded ? <ChevronUp size={14} className="text-muted-foreground" /> : <ChevronDown size={14} className="text-muted-foreground" />}
                    </div>
                  </div>

                  {/* Quick stats row */}
                  <div className="flex items-center gap-4 mt-2 flex-wrap">
                    <span className="text-xs text-foreground font-semibold">{item.quantity} <span className="text-muted-foreground font-normal">{item.uom}</span></span>
                    {item.rack && (
                      <span className="flex items-center gap-1 text-2xs text-muted-foreground">
                        <Layers size={11} />
                        {item.rack}
                      </span>
                    )}
                    <div className="flex-1 min-w-[120px] max-w-[160px]">
                      <ShelfLifeBar pct={item.shelfLifePct} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Expanded details */}
              {isExpanded && (
                <div className="px-5 pb-5 border-t border-border pt-4 space-y-4 fade-in">
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-muted/30 rounded-lg px-3 py-2.5">
                      <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1"><Hash size={10} /> Batch</p>
                      <p className="text-xs font-mono font-bold text-primary">{item.batchNumber}</p>
                    </div>
                    <div className="bg-muted/30 rounded-lg px-3 py-2.5">
                      <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1"><Calendar size={10} /> Mfg Date</p>
                      <p className="text-xs font-semibold text-foreground">{item.mfgDate || '—'}</p>
                    </div>
                    <div className="bg-muted/30 rounded-lg px-3 py-2.5">
                      <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1"><Calendar size={10} /> Expiry</p>
                      <p className={`text-xs font-semibold ${isLowShelf ? 'text-red-400' : 'text-foreground'}`}>{item.expiryDate || '—'}</p>
                    </div>
                    <div className="bg-muted/30 rounded-lg px-3 py-2.5">
                      <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1 flex items-center gap-1"><Layers size={10} /> Rack</p>
                      <p className="text-xs font-mono font-semibold text-foreground">{item.rack || 'Not assigned'}</p>
                    </div>
                  </div>

                  {item.remarks && (
                    <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-accent/8 border border-accent/20">
                      <AlertTriangle size={13} className="text-accent flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-accent">{item.remarks}</p>
                    </div>
                  )}

                  {/* QC Note input */}
                  {item.status === 'pending' && (
                    <div className="flex flex-col gap-1.5">
                      <label className="text-2xs font-semibold text-muted-foreground uppercase tracking-wider">QC Note (optional)</label>
                      <textarea
                        value={noteInputs[item.id] || ''}
                        onChange={(e) => setNoteInputs((prev) => ({ ...prev, [item.id]: e.target.value }))}
                        placeholder="Enter reason for approval or rejection…"
                        rows={2}
                        className="w-full bg-muted/40 border border-border rounded-lg px-3 py-2.5 text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-all resize-none"
                      />
                    </div>
                  )}

                  {item.qcNote && item.status !== 'pending' && (
                    <div className="px-3 py-2.5 rounded-lg bg-muted/30 border border-border">
                      <p className="text-2xs text-muted-foreground uppercase tracking-wider mb-1">QC Note</p>
                      <p className="text-xs text-foreground">{item.qcNote || 'No note provided'}</p>
                    </div>
                  )}

                  {/* Action buttons */}
                  {item.status === 'pending' ? (
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => handleApprove(item.id)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-primary text-primary-foreground text-sm font-bold hover:bg-primary/90 transition-all"
                      >
                        <CheckCircle2 size={16} />
                        Approve
                      </button>
                      <button
                        onClick={() => handleReject(item.id)}
                        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-red-500/15 text-red-400 border border-red-500/30 text-sm font-bold hover:bg-red-500/25 transition-all"
                      >
                        <XCircle size={16} />
                        Reject
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <p className={`text-xs font-semibold ${item.status === 'approved' ? 'text-primary' : 'text-red-400'}`}>
                        {item.status === 'approved' ? '✓ Item approved — moved to Available stock' : '✗ Item rejected — marked as Damaged/Hold'}
                      </p>
                      <button
                        onClick={() => handleUndo(item.id)}
                        className="text-2xs text-muted-foreground hover:text-foreground underline transition-colors"
                      >
                        Undo
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
